import { Button, Input, Select } from 'antd';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import styled from 'styled-components';

import DynamicGroup from 'stores/DynamicProperties/Types/Group';
import { DynamicPropertyType } from 'types/DynamicPropertyType';
import { camelCaseToTitle } from 'utils/String';

import PropertyPopover from '../PropertyPopover';
import InputContainer from './InputContainer';

export const Container = styled.div`
    display: flex;
    width: 100%;
    margin: 18px 0 10px;
    padding: 10px 10px 3px;
    border: 1px solid #e2e2e2;
    border-radius: 4px;
    position: relative;
`;

const GroupContainer = styled.div`
    padding-top: 5px;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    width: 100%;
`;

const GroupLabel = styled.div`
    background: white;
    display: flex;
    position: absolute;
    font-size: 13px;
    font-weight: bold;
    top: -17px;
    left: 8px;
    align-items: center;
    justify-content: center;
    padding: 5px 6px;
`;

const AddPropertyPanel = styled.span`
    display: flex;
    margin-bottom: 5px;
`;

export interface InputGroupProps {
    disabled?: boolean;
    editMode?: boolean;
    hideLabel?: boolean;

    // DynamicPropertiesManager extends DynamicGroup, so naming it here adds
    // nothing the base does not already admit.
    property: DynamicGroup | undefined;
}

/**
 * Whether a user may add each property type from the Add Property panel.
 *
 * This is a Record rather than a Set so TS enforces exhaustive keys.
 */
const ADDABLE: Record<DynamicPropertyType, boolean> = {
    [DynamicPropertyType.ASSET]: true,
    [DynamicPropertyType.BOOLEAN]: true,
    [DynamicPropertyType.COLOR]: true,
    [DynamicPropertyType.DIMENSIONS]: true,
    [DynamicPropertyType.GROUP]: true,
    [DynamicPropertyType.LABEL]: true,
    [DynamicPropertyType.NUMBER]: true,
    [DynamicPropertyType.PERCENTAGE]: true,
    [DynamicPropertyType.PIXELS]: true,
    [DynamicPropertyType.SVGText]: true,
    [DynamicPropertyType.TEMPLATE]: true,
    [DynamicPropertyType.TEXT]: true,

    // SVGText's internal render cache, not something a user authors
    [DynamicPropertyType.SVGData]: false,
};

const ADDABLE_TYPES: ReadonlyArray<DynamicPropertyType> = Object.values(
    DynamicPropertyType
).filter((type) => ADDABLE[type]);

function asAddableType(value: unknown): DynamicPropertyType | undefined {
    return ADDABLE_TYPES.find((type) => type === value);
}

function InputGroup({
    disabled = false,
    editMode = false,
    hideLabel = false,
    property,
}: InputGroupProps) {
    const [newPropertyName, setNewPropertyName] = useState('');

    // undefined until a type is chosen: 'no selection' is a state, not a
    // DynamicPropertyType, so it stays out of band and the type checker
    // keeps it from reaching anything that expects a real type.
    const [newPropertyType, setNewPropertyType] = useState<
        DynamicPropertyType | undefined
    >(undefined);

    if (!property || !(property instanceof DynamicGroup)) {
        return null;
    }

    const childProperties = property.properties;

    const canAddProperty =
        editMode && !!newPropertyName && newPropertyType !== undefined;

    const handleAddProperty = () => {
        if (canAddProperty) {
            // Pass a value of `null` to (eventually) invoke setDefault
            property.add(newPropertyName, newPropertyType, null);
        }
    };

    return (
        <Container>
            {property.name && !hideLabel && (
                <PropertyPopover property={property} editMode={editMode}>
                    <GroupLabel>{camelCaseToTitle(property.name)}</GroupLabel>
                </PropertyPopover>
            )}

            <GroupContainer>
                {childProperties.map((property) => {
                    return (
                        <InputContainer
                            key={property.name}
                            property={property}
                            disabled={disabled}
                            editMode={editMode}
                        />
                    );
                })}

                {editMode && (
                    <AddPropertyPanel>
                        <Select
                            showSearch
                            style={{ width: 200, marginRight: '4px' }}
                            placeholder="Select type"
                            optionFilterProp="children"
                            onChange={(value) => {
                                const type = asAddableType(value);
                                if (!type) {
                                    console.warn(
                                        `[InputGroup] Ignoring property type` +
                                            ` ${JSON.stringify(value)}, which is` +
                                            ` not addable`
                                    );
                                    return;
                                }
                                setNewPropertyType(type);
                            }}
                        >
                            {ADDABLE_TYPES.map((type) => (
                                <Select.Option key={type} value={type}>
                                    {type}
                                </Select.Option>
                            ))}
                        </Select>

                        <Input
                            style={{ marginRight: '4px' }}
                            placeholder="Property name"
                            onChange={(e) => {
                                setNewPropertyName(e.target.value);
                            }}
                        />

                        <Button
                            disabled={!canAddProperty}
                            onClick={handleAddProperty}
                        >
                            Add Property
                        </Button>
                    </AddPropertyPanel>
                )}
            </GroupContainer>
        </Container>
    );
}

export default observer(InputGroup);
